import { PayloadAction } from "@reduxjs/toolkit";
import { push } from "connected-react-router";
import { SagaIterator } from "redux-saga";
import { call, put, select, takeLatest } from "redux-saga/effects";
import factory from "utils/getApiHandlerEffect";

import { selectActivationCode } from "scenes/selectors";
import { PasswordPolicyResponse } from "types/PasswordPolicy";
import { UserInfo } from "types/UserInfo";
import { APIError, APIResponseError } from "types/ApiErrorResponse";
import storage from "../../persistance";
import {
  afterValidateUsernameUnique,
  afterValidateUsernameUniqueFailure,
  afterLoadingUserInfo,
  afterPasswordPoliciesLoad,
  afterTermsLoad,
  getTermsAndConditionFile,
  loadPasswordPolicies,
  loadUserInfo,
  onPasswordActivationFailure,
  onSuccessSubmitPassword,
  onUserActivation,
  setInvalidCode,
  // setRetries,
  setTermsAndCondition,
  termsAndConditionFailure,
  validateUsernameUnique,
} from "./redux";
import { selectRetries } from "./selectors";

const isUniqueValidationResponse = (response: any): boolean => {
  if (typeof response?.isUnique === "boolean") {
    return response.isUnique;
  }
  if (typeof response?.available === "boolean") {
    return response.available;
  }
  if (typeof response?.valid === "boolean") {
    return response.valid;
  }

  return true;
};

const isDuplicateError = (error: APIError): boolean => {
  if (error.statusCode === 409) {
    return true;
  }

  if (error.statusCode === 400 && error.body) {
    try {
      const parsed: APIResponseError = JSON.parse(error.body);
      return (
        !!parsed?.message &&
        parsed.message.toLowerCase().includes("already") &&
        parsed.message.toLowerCase().includes("username")
      );
    } catch (e) {
      return false;
    }
  }

  return false;
};

const getDuplicateUsernameError = (error: APIError): string | null => {
  if (error.body) {
    try {
      const parsed: APIResponseError = JSON.parse(error.body);
      const message = parsed.errors?.[0]?.message ?? parsed.message;

      return parsed.status === 409 && message ? message : null;
    } catch (e) {
      return null;
    }
  }

  return null;
};

export function* onLoadPasswordPolicies(): SagaIterator<void> {
  const activationCode = yield select(selectActivationCode);
  const apiHandler: APIHandler = yield call(() => factory());
  if (!activationCode) {
    yield put(setInvalidCode());
    return;
  }
  try {
    const policies: PasswordPolicyResponse = yield call(() =>
      apiHandler.loadPasswordPolicies(activationCode)
    );
    yield put(afterPasswordPoliciesLoad(policies));
  } catch (e: any) {
    yield put(
      afterPasswordPoliciesLoad({
        settings: {
          password: {
            complexity: {},
            age: {},
            lockout: {},
          },
        },
      })
    );
  }
}

export function* onLoadUserInfo(): SagaIterator<any> {
  const activationCode = yield select(selectActivationCode);
  if (!activationCode) {
    yield put(setInvalidCode());
    return;
  }
  const apiHandler: APIHandler = yield call(() => factory());

  const userInfo: UserInfo = yield call(() =>
    apiHandler.getUserInfo(activationCode)
  );
  yield put(afterLoadingUserInfo(userInfo));
}

export function* onSubmitPassword(
  action: PayloadAction<any>
): SagaIterator<void> {
  const { value } = action.payload;
  let activationCode = yield select(selectActivationCode);
  const noOfRetries = yield select(selectRetries);
  const apiHandler = yield call(() => factory());
  if (noOfRetries > 0 && noOfRetries < 3) {
    activationCode = storage.getItem("SESSION_KEY_RETRY");
  }

  yield put(
    onPasswordActivationFailure({
      hasFailed: false,
      apiError: null,
      usernameUpdateError: null,
    })
  );
  if (!activationCode) {
    yield put(setInvalidCode());
    return;
  }

  try {
    // Username is submitted together with password in the same activation API call.
    yield call(() =>
      apiHandler.submitPasswordActivation(activationCode, value)
    );
    yield put(
      onSuccessSubmitPassword({
        isAccountActivated: true,
        updatedUsername: value.username,
      })
    );
    yield put(push("/security"));
  } catch (e: unknown) {
    const error = e as APIError;
    const usernameUpdateError = getDuplicateUsernameError(error);

    if (usernameUpdateError) {
      yield put(
        onPasswordActivationFailure({
          hasFailed: false,
          apiError: null,
          usernameUpdateError,
        })
      );
      return;
    }

    yield put(
      onPasswordActivationFailure({
        hasFailed: true,
        apiError:
          error.statusCode === 400 && error.body
            ? JSON.parse(error.body).message
            : null,
      })
    );
  }
}

export function* onValidateUsernameUnique(
  action: PayloadAction<{ username: string }>
): SagaIterator<void> {
  const { username } = action.payload;
  const activationCode = yield select(selectActivationCode);
  const apiHandler: APIHandler = yield call(() => factory());

  if (!activationCode) {
    yield put(
      afterValidateUsernameUniqueFailure({
        username,
        errorType: "GENERIC",
      })
    );
    return;
  }

  try {
    const response = yield call(() =>
      apiHandler.validateActivationUsername(activationCode, { username })
    );
    const isUnique = isUniqueValidationResponse(response);

    if (!isUnique) {
      yield put(
        afterValidateUsernameUniqueFailure({
          username,
          errorType: "DUPLICATE",
        })
      );
      return;
    }

    yield put(afterValidateUsernameUnique({ username, isUnique: true }));
  } catch (e: unknown) {
    const error = e as APIError;
    yield put(
      afterValidateUsernameUniqueFailure({
        username,
        errorType: isDuplicateError(error) ? "DUPLICATE" : "GENERIC",
      })
    );
  }
}

export function* termsAndCondition(
  action: PayloadAction<any>
): SagaIterator<void> {
  const terms = action.payload;
  const apiHandler = yield call(() => factory());
  const activationCode = yield select(selectActivationCode);
  if (!activationCode) {
    yield put(setInvalidCode());
    return;
  }
  try {
    const selectedValidator: any = yield call(() =>
      apiHandler.setTermsAndConditionStatus(activationCode, { accepted: terms })
    );
    yield put(onSuccessSubmitPassword(selectedValidator));
  } catch (e: any) {
    yield put(onSuccessSubmitPassword(terms));
    yield put(termsAndConditionFailure());
  }
}

export function* getTermsAndCondition(): SagaIterator<void> {
  try {
    const apiHandler: APIHandler = yield call(() => factory());
    const activationCode = yield select(selectActivationCode);
    if (!activationCode) {
      yield put(setInvalidCode());
      return;
    }
    const data = yield call(() =>
      apiHandler.getTermsAndCondition(activationCode)
    );

    const text = yield call(() => new Response(data).text());

    yield put(afterTermsLoad(text));
  } catch (error) {
    //  yield put(setError(true));
  }
}

// watcher function
export default function* passwordSetupSaga() {
  yield takeLatest(loadPasswordPolicies.type, onLoadPasswordPolicies);
  yield takeLatest(onUserActivation.type, onSubmitPassword);
  yield takeLatest(loadUserInfo.type, onLoadUserInfo);
  yield takeLatest(setTermsAndCondition.type, termsAndCondition);
  yield takeLatest(getTermsAndConditionFile.type, getTermsAndCondition);
  yield takeLatest(validateUsernameUnique.type, onValidateUsernameUnique);
}
